import { h, render } from "preact";
import { z } from "zod";

import {
  pickerInspectResponseSchema,
  pickerSaveResponseSchema,
} from "@go-novus/admin-api";

import type { ResolvedWidgetConfig } from "../config";
import {
  createModalParking,
  isInModalHost,
  type ModalParking,
} from "../modal-parking";
import { deriveTheme } from "../ui/theme";
import {
  OVERLAY_Z_INDEX,
  PickerOverlay,
  type PickerView,
} from "./picker-overlay";
import { buildUniqueSelector, isUniqueMatch } from "./selector";

/** 店面 URL 上携带拾取令牌的查询参数；读入后立即从地址栏摘除。 */
const URL_PARAM = "gonovus_picker";
/** 覆盖层挂载点的稳定 id，测试与排查都从它进入。 */
export const PICKER_OVERLAY_ID = "gonovus-picker";

/** 令牌长度上限，与 admin-api 的 picker 请求契约一致（min 1 / max 128）。 */
const TOKEN_MAX_LEN = 128;
const storedSchema = z.strictObject({
  token: z.string().min(1).max(TOKEN_MAX_LEN),
});
type Stored = z.infer<typeof storedSchema>;

export interface PickerHandle {
  dispose: () => void;
}

/**
 * 店面元素选择器：消费绑定规则的拾取令牌，让商家为指定规则拾取目标元素。
 * 随挂件打在同一 IIFE 里，不单独拆包——店面资产走主题扩展的 assets，
 * 拆包意味着第二个 script 与加载时序。预算上限 raw 300KB 或 gzip
 * 90KB：触及任一就重新评估拆分（异步 chunk 或独立资产），在那之前
 * 保持单文件。
 *
 * 授权上下文（令牌）存 sessionStorage：同标签同源导航后挂件重挂，
 * 从这里恢复并重新只读校验。令牌只走请求体——查询参数会被 App Proxy
 * 鉴权在找不到 offline Session 时整串写进日志。
 */
export function createElementPicker(
  container: HTMLElement,
  config: ResolvedWidgetConfig,
  shadowRoot: ShadowRoot,
): PickerHandle | undefined {
  // 拾取端点与握手共用 App Proxy 前缀：…/handshake → …/picker{,/inspect}。
  const proxyBase = config.handshakeUrl.replace(/\/handshake$/, "");
  const pickerUrl = `${proxyBase}/picker`;
  const inspectUrl = `${pickerUrl}/inspect`;
  const storageKey = `gonovus:picker:${config.shopDomain}`;
  const theme = deriveTheme(config.appearance);
  const font = config.appearance.font ?? "inherit";
  const fontSize = config.appearance.fontSize ?? "14px";

  let disposed = false;
  /** Esc 在令牌校验途中按下：中止进入，在途响应不再驱动界面。 */
  let aborted = false;
  let view: PickerView | undefined;
  let overlayRoot: HTMLElement | undefined;
  let boxEl: HTMLElement | undefined;
  let barEl: HTMLElement | undefined;
  /** 覆盖层的寄居位：topmost modal 开了就跟进去，停了才回挂件 shadow。 */
  let parking: ModalParking | undefined;
  let hovered: Element | undefined;
  /** 已确认等待保存的元素；confirm 相的高亮、文案与写入都锁定它。 */
  let confirmedEl: Element | undefined;
  /** inspect 绑定的规则 id：保存响应要按它确认这次写入属于哪条规则。 */
  let boundRuleId: string | undefined;
  let rulePrompt = "";

  /** sessionStorage 不可用时只在本次挂载内保住令牌，换页即失效。 */
  let memoryToken: string | undefined;
  const readStored = (): string | undefined => {
    try {
      const parsed = storedSchema.safeParse(
        JSON.parse(sessionStorage.getItem(storageKey) ?? "null") as unknown,
      );
      if (parsed.success) return parsed.data.token;
      sessionStorage.removeItem(storageKey);
    } catch {
      /* 隐私模式读不到就按没有处理。 */
    }
    return memoryToken;
  };
  const writeStored = (token: string) => {
    memoryToken = token;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ token } satisfies Stored),
      );
    } catch {
      /* 存不下就只剩内存令牌，本页内拾取仍可用。 */
    }
  };
  const clearStored = () => {
    memoryToken = undefined;
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      /* 同上。 */
    }
  };

  /**
   * 从地址栏取令牌并立刻摘除全部同名参数（含重复与空值），只在历史里
   * 留干净 URL。返回 undefined 表示参数没出现——同标签换页，沿用本地
   * 授权；null 表示参数显式为空或全是不可用值——明确无效的授权意图，
   * 不得回退到旧令牌去校验另一条规则。
   */
  const takeUrlToken = (): string | null | undefined => {
    const url = new URL(location.href);
    if (!url.searchParams.has(URL_PARAM)) return undefined;
    const values = url.searchParams.getAll(URL_PARAM);
    url.searchParams.delete(URL_PARAM);
    history.replaceState(
      history.state,
      "",
      url.pathname + url.search + url.hash,
    );
    // 服务端契约上限：超长的显式值与空值一样按无效授权意图处理。
    return (
      values.find((value) => value !== "" && value.length <= TOKEN_MAX_LEN) ??
      null
    );
  };

  const urlToken = takeUrlToken();
  let token: string | undefined;
  if (urlToken === undefined) {
    token = readStored();
  } else if (urlToken === null) {
    clearStored();
  } else {
    writeStored(urlToken);
    token = urlToken;
  }
  if (!token) return undefined;

  /** 拾取请求全是 POST + JSON 请求体：令牌与选择器只走这里。 */
  const postJson = (url: string, body: unknown) =>
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

  // 令牌一出现就压住主动消息并接住 Esc：校验在途时商家也可能想退出。
  container.dataset.pickerActive = "true";
  window.addEventListener("keydown", onKeydown, true);

  /**
   * 只有 pick 相响应悬停与 Enter。confirm 相的目标已锁定——鼠标滑过别的
   * 元素去够控制条不许换高亮，重选只能走确认条上的 Pick again。
   */
  function selecting() {
    return view?.phase === "pick";
  }

  function positionBox(element: Element) {
    if (!boxEl) return;
    const rect = element.getBoundingClientRect();
    boxEl.style.display = "block";
    boxEl.style.left = `${String(rect.left)}px`;
    boxEl.style.top = `${String(rect.top)}px`;
    boxEl.style.width = `${String(rect.width)}px`;
    boxEl.style.height = `${String(rect.height)}px`;
  }

  function onHover(event: MouseEvent) {
    if (!selecting()) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    // 事件穿越 Shadow DOM 时 target 重定向成宿主容器：悬停在挂件自身
    // （含拾取控制条）上不算拾取页面元素。
    if (target === container || container.contains(target)) return;
    // 寄居在 modal 里的挂件 UI（拾取覆盖层、买家挂件）都不算拾取页面
    // 元素：寄居 shadow 里的事件 retarget 成寄居宿主本身。
    if (isInModalHost(target)) return;
    hovered = target;
    positionBox(target);
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      // IME 合成中的 Esc 是取消候选词，不是退出拾取。
      if (!event.isComposing) {
        // 拾取模式活着时 Esc 归我们消费：preventDefault 让原生 dialog
        // 不跟着取消。不 stopPropagation——主题里按 Esc 关自定义弹层的
        // 脚本照常跑，那只算页面原有行为，不归我们拦。
        event.preventDefault();
        exit();
      }
      return;
    }
    if (event.key !== "Enter" || event.isComposing || !selecting()) return;
    // event.target 穿越 Shadow DOM 时 retarget 成宿主；composedPath 的
    // 第一个才是真实目标，第三方 open shadow 里的 input 也能认出来。
    // closed shadow 从外部不可穿透，retarget 后的宿主依旧认不出，这是
    // 与跨域 iframe 同类的已知边界。
    const [innermost] = event.composedPath();
    const target =
      innermost instanceof Element
        ? innermost
        : event.target instanceof Element
          ? event.target
          : undefined;
    if (!target) return;
    // 挂件自身（拾取控制条、聊天面板）里的 Enter 不算拾取页面元素；
    // 寄居进 modal 的那部分由 isInModalHost 兜住。
    if (
      target === container ||
      container.contains(target) ||
      target.getRootNode() === shadowRoot ||
      isInModalHost(target)
    )
      return;
    // 输入控件里的 Enter 照常换行或提交，不当作选中。
    if (
      target.closest(
        'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
      )
    )
      return;
    if (!hovered?.isConnected) return;
    // 消费这次 Enter：阻止它去激活当前聚焦的链接或按钮。
    event.preventDefault();
    select(hovered);
  }

  /** 滚动/缩放时把高亮框重新贴到当前目标：pick 跟悬停，confirm 锁定已确认元素。 */
  function reposition() {
    const target =
      view?.phase === "confirm"
        ? confirmedEl
        : view?.phase === "pick"
          ? hovered
          : undefined;
    // 元素被移除就停在原位置，不画一个 0x0 的框到左上角。
    if (target?.isConnected) positionBox(target);
  }

  function select(element: Element) {
    const selector = buildUniqueSelector(element);
    // 构造上天然唯一；再按当前页面唯一命中校验一次，不满足就不进确认态。
    if (!isUniqueMatch(selector, element)) return;
    confirmedEl = element;
    show({ phase: "confirm", prompt: rulePrompt, selector, saving: false });
  }

  /** Esc 中止或挂件卸载都会结束拾取会话，在途响应不再驱动界面。 */
  function sessionOver() {
    return disposed || aborted;
  }

  /**
   * 保存把绑定令牌与选择器一起交给服务端：令牌决定写哪条规则，
   * 服务端事务内原子消费。发送前复查目标在当前页仍唯一；响应按
   * pickerSaveResponseSchema 确认属于绑定规则才算成功，无法确认的
   * 2xx 按可安全重试处理（重试撞已消费令牌会走 401）。401/409 令牌
   * 或规则已不可用，引导回后台；400 是选择器被拒，只能重选；其余是
   * 可重试的传输或服务端失败。
   */
  async function save() {
    if (view?.phase !== "confirm" || view.saving) return;
    const selector = view.selector;
    // Enter 时唯一不代表保存时仍唯一：目标被移除或选择器变成多命中时
    // 提示重选，不发送写入。
    if (!confirmedEl || !isUniqueMatch(selector, confirmedEl)) {
      backToConfirm(selector, "selector");
      return;
    }
    show({ ...view, saving: true, failure: undefined });
    let response: Response;
    try {
      response = await postJson(pickerUrl, { token, selector });
    } catch {
      if (!sessionOver()) backToConfirm(selector, "network");
      return;
    }
    // 保存途中退出（Esc/卸载）后到达的响应不再驱动界面；已写入与否
    // 由后台重新读取规则来确认。
    if (sessionOver()) return;
    if (response.ok) {
      // 只有按契约确认属于本次绑定规则的写入才算成功：2xx 但解析不出、
      // 缺字段或 ACK 对不上绑定规则时写入状态未知，按可安全重试处理——
      // 若其实已写入并消费令牌，重试会撞 401，照"已消费"引导回后台。
      const ack = pickerSaveResponseSchema.safeParse(
        (await response.json().catch(() => null)) as unknown,
      );
      if (sessionOver()) return;
      // 服务端回显写入值（schema 带 trim）；我们生成的选择器没有首尾空白，
      // 回显与发送值应逐字相等。
      if (
        ack.success &&
        ack.data.ruleId === boundRuleId &&
        ack.data.selector === selector
      ) {
        teardown(true);
        // 后台通过标签关闭结清会话：重选同一目标也能释放保存保护。
        window.close();
        return;
      }
      backToConfirm(selector, "network");
      return;
    }
    if (response.status === 401 || response.status === 409) {
      clearStored();
      show({
        phase: "closed",
        tone: response.status === 401 ? "consumed" : "gone",
      });
      return;
    }
    backToConfirm(selector, response.status === 400 ? "selector" : "network");
  }

  function backToConfirm(selector: string, failure: "network" | "selector") {
    show({
      phase: "confirm",
      prompt: rulePrompt,
      selector,
      saving: false,
      failure,
    });
  }

  function draw() {
    // draw 只在 show 之后被调用：view 一定已就位，barEl 是渲染目标。
    if (!view || !barEl) return;
    render(
      h(PickerOverlay, {
        view,
        theme,
        font,
        fontSize,
        onExit: exit,
        onReselect() {
          // 只有确认相（且不在写入中）能重选；回到 pick 后重新悬停拾取。
          if (view?.phase !== "confirm" || view.saving) return;
          confirmedEl = undefined;
          hovered = undefined;
          if (boxEl) boxEl.style.display = "none";
          show({ phase: "pick", prompt: rulePrompt });
        },
        onSave() {
          void save();
        },
      }),
      barEl,
    );
  }

  function ensureOverlay() {
    if (overlayRoot) return;
    overlayRoot = document.createElement("div");
    overlayRoot.id = PICKER_OVERLAY_ID;
    // 高亮框由控制器直接跟随指针定位，不走 Preact 渲染，barEl 才是渲染目标。
    boxEl = document.createElement("div");
    boxEl.setAttribute("aria-hidden", "true");
    boxEl.style.display = "none";
    boxEl.style.position = "fixed";
    boxEl.style.pointerEvents = "none";
    boxEl.style.zIndex = String(OVERLAY_Z_INDEX);
    boxEl.style.border = `2px solid ${theme.accent}`;
    boxEl.style.borderRadius = "4px";
    boxEl.style.background = theme.accentSoft;
    boxEl.style.boxSizing = "border-box";
    barEl = document.createElement("div");
    overlayRoot.append(boxEl, barEl);
    // 覆盖层跟随 topmost modal 寄居；停在 home 也常驻 top layer——manual
    // popover 压在 modal 之上，又不制造 inert、没有 light-dismiss 和
    // Esc 自关，交互全部留在我们自己的监听里。modal 跟踪、寄居宿主与
    // 搬家细节都在 modal-parking 里。
    // 同批 modal 变化下各 parking 按观察器注册顺序重抬：买家挂件的
    // parking 在挂载时就建好，本实例进入拾取才创建、后抬——覆盖层因此
    // 始终压在寄居的买家 UI 之上。
    parking = createModalParking({
      hostTag: "gonovus-picker-host",
      home: shadowRoot,
      content: overlayRoot,
      raise: "always",
    });
    draw();
  }

  function show(next: PickerView) {
    view = next;
    ensureOverlay();
    if (next.phase === "closed" && boxEl) boxEl.style.display = "none";
    draw();
  }

  function enter(prompt: string) {
    rulePrompt = prompt;
    container.dataset.pickerActive = "true";
    window.addEventListener("mouseover", onHover, { passive: true });
    window.addEventListener("scroll", reposition, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", reposition, { passive: true });
    show({ phase: "pick", prompt });
  }

  /** 结束拾取：卸监听、覆盖层与标记。clear 为 true 时连同本地授权一起清。 */
  function teardown(clear: boolean) {
    if (clear) clearStored();
    view = undefined;
    hovered = undefined;
    confirmedEl = undefined;
    // 寄居收拾由 parking 负责：撤宿主、内容停回家、收掉顶层展示。
    parking?.dispose();
    parking = undefined;
    delete container.dataset.pickerActive;
    window.removeEventListener("mouseover", onHover);
    window.removeEventListener("keydown", onKeydown, true);
    window.removeEventListener("scroll", reposition, true);
    window.removeEventListener("resize", reposition);
    if (overlayRoot) {
      if (barEl) render(null, barEl);
      overlayRoot.remove();
      overlayRoot = undefined;
      boxEl = undefined;
      barEl = undefined;
    }
  }

  function exit() {
    aborted = true;
    teardown(true);
  }

  /** 校验没通过就没进入：卸下拾取意图，但保留授权给下一次挂载。 */
  function disarm() {
    delete container.dataset.pickerActive;
    window.removeEventListener("keydown", onKeydown, true);
  }

  void postJson(inspectUrl, { token })
    .then(async (response) => {
      if (sessionOver()) return;
      if (!response.ok) {
        // 只有令牌失效或规则不可用才算终审；其余状态是服务端瞬时
        // 失败，不进拾取、保留授权，下一次挂载重新校验。
        if (response.status === 401 || response.status === 409) {
          delete container.dataset.pickerActive;
          clearStored();
          show({
            phase: "closed",
            tone: response.status === 409 ? "gone" : "invalid",
          });
        } else {
          disarm();
        }
        return;
      }
      const parsed = pickerInspectResponseSchema.safeParse(
        (await response.json().catch(() => null)) as unknown,
      );
      if (sessionOver()) return;
      if (!parsed.success) {
        disarm();
        return;
      }
      boundRuleId = parsed.data.ruleId;
      enter(parsed.data.prompt);
    })
    .catch(() => {
      /* 传输失败不进入也不清授权：下一次挂载重新校验。 */
      disarm();
    });

  return {
    dispose() {
      if (disposed) return;
      disposed = true;
      teardown(false);
    },
  };
}
